import { Avatar } from "@avenueops/web-components";
import _ from "lodash";
import React, { ReactNode } from "react";
import Select, { OptionsOrGroups, GroupBase } from "react-select";
import { v4 as uuidv4 } from "uuid";

export type Option = {
  value?: string | Record<string, any>;
  label: string;
  iconUrl?: string;
};

export type Group = GroupBase<Option>;

interface Props {
  title?: string;
  placeholder: string;
  className?: string;
  options: OptionsOrGroups<Option, Group>;
  error?: boolean;
  onCreateOption?: any;
  disabled?: boolean;
  onChange: (_: any) => void;
  val: Option["value"];
  additionalStyles?: any;
  clearable?: boolean;
  textSize?: string;
  textColor?: string;
  passOption?: boolean;
  isMulti?: boolean;
  defaultMenuIsOpen?: boolean;
  secondaryElement?: ReactNode;
}

function getValueFromOptionsList(options: Option[], val: any) {
  return options.find(({ value }) => value === val) ?? { value: val, label: val };
}

function getValueFromOptionGroupsList(groups: Group[], val: any) {
  for (const group of groups) {
    const value = group?.options?.find(({ value }) => value === val);
    if (value) {
      return value;
    }
  }
  return { value: val, label: val };
}

export default function UncontrolledInputSearchableSelect({
  title,
  defaultMenuIsOpen,
  disabled,
  placeholder,
  className,
  options,
  error,
  onChange,
  val,
  additionalStyles,
  clearable = true,
  textSize,
  textColor,
  passOption = false,
  isMulti = false,
  secondaryElement,
}: Props): JSX.Element {
  const formInputId = React.useMemo(() => {
    return uuidv4();
  }, []);

  const value =
    options.length === 0
      ? { value: val, label: val }
      : "options" in options[0]
      ? getValueFromOptionGroupsList(options as Group[], val)
      : getValueFromOptionsList(options as Option[], val);

  return (
    <>
      {title ? (
        <label htmlFor={formInputId} className="mb-1 flex flex-row gap-2 text-sm font-medium leading-5 text-gray-700">
          {title}
          {error ? <span className="text-red-500 "> required</span> : null}
          {secondaryElement}
        </label>
      ) : null}
      <div className="remove-input-txt-border">
        <Select
          defaultMenuIsOpen={defaultMenuIsOpen ?? false}
          isMulti={isMulti}
          isClearable={false}
          defaultValue={undefined}
          isDisabled={disabled}
          placeholder={placeholder}
          onChange={(option) => (option ? onChange(passOption ? option : (option as Option).value) : onChange(null))}
          value={passOption ? value.value : value}
          options={options}
          theme={(theme) => ({
            ...theme,
          })}
          styles={{
            control: (base) => ({
              ...base,
              ...(additionalStyles ? additionalStyles : {}),
              borderRadius: 2,
              borderColor: "#9ca3af",
            }),
            // TOOD: Figue out how to hide the cursor here
            // input: (base) => ({
            //   ...base,
            //   isHidden: true,
            // }),
          }}
          formatOptionLabel={(option: Option) => (
            <div
              className={`
                ${textSize ? textSize : ""}
                ${textColor ? textColor : ""}
              `}
            >
              {option.iconUrl ? (
                <div className="flex justify-start items-center truncate">
                  {/* TODO: Figure out avatar shrinking issue for long names */}
                  <Avatar src={option.iconUrl} size="SMALL" /> <span className="ml-2">{option.label}</span>
                </div>
              ) : (
                option.label
              )}
            </div>
          )}
          className={` 
            ${className}
            sm:text-sm sm:leading-5 rounded
            ${error ? "border-red-200 outline-none ring ring-red-200 ring-opacity-50" : ""}`}
        />
      </div>
    </>
  );
}
